/** #434 (SPEC-434 3.1): Commissions > Grids. Grids grouped by insurer, with live version and staged count. */
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/layout';
import { Badge, Button, Card, EmptyState, LoadingSpinner, Select } from '@/components/common';
import { commissionGridService, type GridListItem } from '@/services/commissionGrids';
import { getErrorMessage } from '@/services/api';
import { useAuthStore } from '@/store/auth';
import { STATUS_LABELS, canWriteGrids, liveVersion } from './gridPages';

const LOB_OPTIONS = [
  { value: '', label: 'All classes' },
  { value: 'pvt_car', label: 'Private car' },
  { value: 'two_wheeler', label: 'Two wheeler' },
  { value: 'gcv', label: 'GCV' },
  { value: 'pcv', label: 'PCV' },
  { value: 'misc', label: 'Misc' },
];

const STATUS_OPTIONS = [{ value: '', label: 'Any status' }].concat(
  Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }))
);

export function GridsListPage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const [lob, setLob] = useState('');
  const [status, setStatus] = useState('');
  const [insurer, setInsurer] = useState('');
  const showBroker = role === 'country_admin' || role === 'super_admin';

  const { data, isLoading, error } = useQuery({
    queryKey: ['commission-grids', lob, status, insurer],
    queryFn: () => commissionGridService.listGrids({ lob, status, insurer }),
  });

  const insurers = useMemo(() => {
    const seen = new Map<string, string>();
    (data ?? []).forEach((g) => seen.set(g.insurer_id, g.insurer_name));
    if (insurer && !seen.has(insurer)) seen.set(insurer, 'Selected insurer');
    return [{ value: '', label: 'All insurers' }].concat(
      Array.from(seen, ([value, label]) => ({ value, label })).sort((a, b) =>
        a.label.localeCompare(b.label)
      )
    );
  }, [data, insurer]);

  const groups = useMemo(() => {
    const byInsurer = new Map<string, GridListItem[]>();
    (data ?? []).forEach((g) =>
      byInsurer.set(g.insurer_name, [...(byInsurer.get(g.insurer_name) ?? []), g])
    );
    return Array.from(byInsurer);
  }, [data]);

  return (
    <div>
      <PageHeader
        title="Commission grids"
        subtitle="Insurer grids, their versions and what is live"
        breadcrumbs={[{ label: 'Commissions', href: '/commissions' }, { label: 'Grids' }]}
        actions={
          canWriteGrids(role) ? (
            <Button onClick={() => navigate('/commissions/grids/import')}>
              Import insurer grid
            </Button>
          ) : undefined
        }
      />
      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <Select
            id="grid-insurer"
            label="Insurer"
            value={insurer}
            options={insurers}
            onChange={(e) => setInsurer(e.target.value)}
          />
          <Select
            id="grid-class"
            label="Class"
            value={lob}
            options={LOB_OPTIONS}
            onChange={(e) => setLob(e.target.value)}
          />
          <Select
            id="grid-status"
            label="Version status"
            value={status}
            options={STATUS_OPTIONS}
            onChange={(e) => setStatus(e.target.value)}
          />
        </div>
      </Card>
      {isLoading && <LoadingSpinner />}
      {error && (
        <p role="alert" className="text-sm text-red-600">
          {getErrorMessage(error)}
        </p>
      )}
      {!isLoading && !error && groups.length === 0 && (
        <EmptyState title="No grids" description="No grid matches these filters." />
      )}
      {groups.map(([insurerName, grids]) => (
        <Card key={insurerName} className="mb-4">
          <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
            {insurerName}
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Grid</th>
                  {showBroker && <th className="py-2 pr-4">Broker</th>}
                  <th className="py-2 pr-4">Classes</th>
                  <th className="py-2 pr-4">Live version</th>
                  <th className="py-2 pr-4">Versions</th>
                  <th className="py-2 pr-4">Staged</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {grids.map((grid) => {
                  const live = liveVersion(grid.versions);
                  const staged = grid.versions.filter((v) => v.status === 'ready').length;
                  const classes = Array.from(new Set(grid.versions.flatMap((v) => v.lobs)));
                  return (
                    <tr key={grid.id} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white">
                        {grid.name}
                      </td>
                      {showBroker && <td className="py-2 pr-4">{grid.broker_name}</td>}
                      <td className="py-2 pr-4">{classes.join(', ') || '-'}</td>
                      <td className="py-2 pr-4">
                        {live ? (
                          <span>
                            v{live.version_no} from {live.effective_from}
                            {live.effective_until ? ` to ${live.effective_until}` : ''}
                          </span>
                        ) : (
                          <Badge variant="default">None live</Badge>
                        )}
                      </td>
                      <td className="py-2 pr-4">{grid.versions.length}</td>
                      <td className="py-2 pr-4">
                        {staged > 0 ? <Badge variant="warning">{staged}</Badge> : 0}
                      </td>
                      <td className="py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/commissions/grids/${grid.id}`)}
                        >
                          Open
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      ))}
    </div>
  );
}
